import { BILLING_PAYMENT_STATUS_PAID_STATUSES } from "../constants/billing";
import {
  BillingPaymentsHistoryResponse,
  BillingPaymentsPaymentResponse,
  BillingPaymentsResponse,
  BillingPaymentsScheduleResponse,
} from "../types";
import {
  BillingPayments,
  BillingPaymentsPayment,
  BillingPaymentsSchedule,
  BillingPaymentStatus,
} from "../types/billing";

const adaptBillingPaymentScheduleResponse = (scheduleResponse: BillingPaymentsScheduleResponse) => {
  return {
    annualDueDate: scheduleResponse.annual_due_date,
    q1DueDate: scheduleResponse.q1_due_date,
    q2DueDate: scheduleResponse.q2_due_date,
    q3DueDate: scheduleResponse.q3_due_date,
    q4DueDate: scheduleResponse.q4_due_date,
  };
};

const adaptBillingPaymentHistoryResponse = (historyResponse: BillingPaymentsHistoryResponse[]) => {
  return historyResponse
    .map(historyItem => ({
      amount: Number(historyItem.amount),
      year: historyItem.year,
    }))
    .sort((a, b) => b.year - a.year);
};

const getBillingDueDate = (
  period: BillingPaymentsPayment["period"],
  schedule: BillingPaymentsSchedule
): BillingPaymentsPayment["dueDate"] | null => {
  const key = (period + "DueDate") as keyof BillingPaymentsSchedule;
  return schedule[key] ?? null;
};

const getBillingPaymentStatus = (
  paymentDate: BillingPaymentsPayment["paymentDate"],
  dueDate: BillingPaymentsPayment["dueDate"],
  previousDueDate: BillingPaymentsPayment["dueDate"] | null
): BillingPaymentStatus => {
  const currentDate = new Date();

  if (paymentDate) return BillingPaymentStatus.Paid;
  if (previousDueDate && new Date(previousDueDate) >= currentDate)
    return BillingPaymentStatus.Upcoming;
  if (dueDate && currentDate > new Date(dueDate)) return BillingPaymentStatus.Overdue;
  return BillingPaymentStatus.Unpaid;
};

const adaptBillingPayments = (
  paymentsResponse: BillingPaymentsPaymentResponse[],
  paymentSchedule: BillingPaymentsSchedule
): BillingPaymentsPayment[] => {
  return paymentsResponse
    .sort((a, b) => a.period.localeCompare(b.period))
    .map((payment, i, arr) => {
      const dueDate = getBillingDueDate(payment.period, paymentSchedule);
      const previousDueDate = i > 0 ? getBillingDueDate(arr[i - 1].period, paymentSchedule) : null;

      const status = getBillingPaymentStatus(payment.payment_date, dueDate, previousDueDate);

      return {
        paymentDate: payment.payment_date,
        period: payment.period,
        amount: Number(payment.amount),
        status,
        dueDate,
      };
    });
};

export const calculateUnpaidAmount = (payments: BillingPaymentsPayment[]) => {
  if (!payments) return 0;

  return payments.reduce((total, payment) => {
    if (BILLING_PAYMENT_STATUS_PAID_STATUSES.includes(payment.status)) return total;
    return total + payment.amount;
  }, 0);
};

export const adaptBillingResponse = (
  billingResponse: BillingPaymentsResponse
): BillingPayments | null => {
  if (!billingResponse.paymentSchedule) return null;

  const paymentSchedule = adaptBillingPaymentScheduleResponse(billingResponse.paymentSchedule);
  const history = adaptBillingPaymentHistoryResponse(billingResponse.history ?? []);
  const payments = adaptBillingPayments(billingResponse.payments ?? [], paymentSchedule);

  return {
    paymentSchedule,
    payments,
    history,
    unpaidAmount: calculateUnpaidAmount(payments),
  };
};
